import { TENANTS, type TenantId, isTenantId } from "./tenants";

const STORAGE_KEY = "myai_excel.tenant";
const LAST_SLUG_KEY = "myai_excel.last_slug";

/**
 * Resolve the active tenant. Order:
 *   1. URL query string (manifest's SourceLocation hint, e.g. ?tenant=caplin).
 *      Wins over localStorage so legacy per-tenant manifests keep working
 *      even if the user has previously picked a different workspace.
 *   2. Previously persisted choice in localStorage.
 *   3. null — caller (Taskpane) renders the TenantPicker.
 *
 * The URL-param branch also writes to localStorage so subsequent navigations
 * within the dialog/popup retain it without re-parsing the URL.
 */
export function resolveTenant(): TenantId | null {
  if (typeof window === "undefined") return null;

  const fromUrl = new URLSearchParams(window.location.search).get("tenant");
  if (isTenantId(fromUrl)) {
    try {
      window.localStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      /* storage unavailable — proceed without persisting */
    }
    return fromUrl;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTenantId(stored)) return stored;
  } catch {
    /* storage unavailable */
  }

  return null;
}

/**
 * Like resolveTenant() but throws if no tenant is set. Use in code paths that
 * are only reachable after the picker / sign-in has completed.
 */
export function requireTenant(): TenantId {
  const t = resolveTenant();
  if (!t) {
    throw new Error("requireTenant: no tenant set — pick a workspace first");
  }
  return t;
}

export function getApiHost(): string {
  return TENANTS[requireTenant()].apiHost;
}

export function getTenantDisplayName(): string {
  return TENANTS[requireTenant()].displayName;
}

/**
 * Persist the user's picker choice. Validates the slug against TENANTS first;
 * unknown slugs return null so callers can show an inline error without
 * leaking which slugs are valid. On match returns the canonicalized TenantId
 * so callers get type-narrowed access.
 */
export function setTenant(slug: string): TenantId | null {
  const trimmed = slug.trim().toLowerCase();
  if (!isTenantId(trimmed)) return null;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, trimmed);
      window.localStorage.setItem(LAST_SLUG_KEY, trimmed);
    } catch {
      /* storage unavailable */
    }
  }
  return trimmed;
}

/**
 * The slug the user typed last — used to pre-fill the picker on Switch
 * workspace so a returning user typically just hits Enter.
 */
export function getLastSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LAST_SLUG_KEY);
  } catch {
    return null;
  }
}

export function clearTenantOverride(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}
